import { BR_FALLBACK, type CountryConfig } from '../country/countryConfig.ts';

/**
 * Where the active country comes from when a caller does not pass one.
 *
 * Injected rather than imported so this module stays a leaf: importing the country
 * *store* would drag zustand and the HTTP client in behind it, which is both a
 * silly dependency for a number formatter and enough to make it untestable under
 * `node --test` (httpClient uses TS parameter properties, which strip-only mode
 * cannot parse).
 *
 * Defaults to Brazil so a formatter called before `main.tsx` registers the real
 * provider still renders something sane.
 */
let resolveCountry: () => CountryConfig = () => BR_FALLBACK;

export function setCountryProvider(provider: () => CountryConfig) {
  resolveCountry = provider;
}

/**
 * Money and number formatting, driven entirely by the active country.
 *
 * No per-country branching anywhere in here: the currency symbol, the decimal
 * separator, the grouping and the number of minor units all come out of `Intl`
 * from two data points — the country's currency and its locale. Adding a market
 * is a `country_registry` row, never a case in a switch.
 *
 * What that buys, concretely: India groups as `1,23,456.79` rather than
 * `123,456.79`, because `en-IN` says so. A hand-rolled formatter with a
 * thousands-separator constant gets that wrong and nobody notices until an
 * Indian player does.
 */

/**
 * The BCP-47 locale to format money in.
 *
 * Language subtag of the country's default locale + the country's own region:
 * `es` + `AR` → `es-AR`, `pt-BR` + `BR` → `pt-BR`, `en` + `IN` → `en-IN`.
 *
 * Region matters more than language here. `es` alone renders ARS as `ARS 1.234`,
 * while `es-AR` gives `$ 1.234` — the form an Argentine actually reads. And this
 * follows the **country**, never the UI language, so a player reading the site in
 * English still sees their own currency formatted the local way (checklist §3.9).
 */
function moneyLocale(country: CountryConfig): string {
  const language = country.defaultLocale.split('-')[0] || 'en';
  return `${language}-${country.code}`;
}

// Intl.NumberFormat construction is not cheap and these run hundreds of times per
// render pass, so formatters are memoised by everything that defines them.
const formatterCache = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  // `notation` belongs in the key: a compact formatter and a plain one can agree
  // on every other field, and serving one for the other would print `$3 M`
  // where an exact figure was asked for.
  const key = `${locale}|${options.style || ''}|${options.currency || ''}|${options.minimumFractionDigits ?? ''}|${options.maximumFractionDigits ?? ''}|${options.notation || ''}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const created = new Intl.NumberFormat(locale, options);
  formatterCache.set(key, created);
  return created;
}

/**
 * How many decimals to show for this currency.
 *
 * The country's registry row wins when it speaks about its own fiat
 * (`fiatDisplayDecimals`, V310): Argentina says 0 because centavos are dead
 * there, and no amount of ISO data would tell us that. Otherwise the answer is
 * asked of `Intl` rather than assumed to be 2 — JPY and CLP have none, and a
 * hardcoded 2 would print `¥ 500.00`. Reading it back from `resolvedOptions()`
 * keeps that knowledge in ICU where it is maintained, not in a table here.
 */
function currencyDecimals(locale: string, currency: string, country?: CountryConfig): number {
  if (country && country.fiatDisplayDecimals != null && currency === country.fiatCurrency) {
    return country.fiatDisplayDecimals;
  }
  try {
    return formatter(locale, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/**
 * Assemble a currency-styled formatter's output, swapping in the market's own
 * symbol where the registry provides one (`fiatSymbol`, V311) and the figure is
 * in that market's fiat. Everything else — spacing, sign placement, grouping —
 * stays exactly what Intl decided for the locale, so `$ 1.500` becomes
 * `AR$ 1.500` and not a hand-assembled string.
 */
function joinCurrencyParts(parts: Intl.NumberFormatPart[], currency: string, country: CountryConfig): string {
  const override = country.fiatSymbol && currency === country.fiatCurrency ? country.fiatSymbol : null;
  return parts
    .map((part) => {
      if (part.type === 'currency' && override) return override;
      if (part.type === 'compact') return normalizeCompactUnit(part.value);
      return part.value;
    })
    .join('');
}

/**
 * Money with its currency symbol.
 *
 * Keeps the long-standing rule that whole amounts render without decimals
 * (`R$ 500`, not `R$ 500,00`) while fractions show the currency's full precision.
 * That is a deliberate product choice, not an accident of the old implementation:
 * stake chips and balances are overwhelmingly whole numbers and the trailing
 * zeros were noise.
 */
export function fmtMoney(value: number | null | undefined, country: CountryConfig = resolveCountry()): string {
  const amount = Number(value);
  const locale = moneyLocale(country);
  const currency = country.fiatCurrency;
  const safe = value === null || value === undefined || Number.isNaN(amount) ? 0 : amount;
  const decimals = Number.isInteger(safe) ? 0 : currencyDecimals(locale, currency, country);
  try {
    return joinCurrencyParts(formatter(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).formatToParts(safe), currency, country);
  } catch {
    // An unknown currency code would otherwise throw and blank the screen. Show
    // the code itself: wrong-looking is recoverable, a missing balance is not.
    return `${currency} ${safe.toFixed(decimals)}`;
  }
}

/** Currency symbol for compact input adornments (for example `R$` or `$`). */
export function fiatCurrencySymbol(country: CountryConfig = resolveCountry()): string {
  if (country.fiatSymbol) return country.fiatSymbol;
  const locale = moneyLocale(country);
  try {
    const part = formatter(locale, {
      style: 'currency',
      currency: country.fiatCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0).find((candidate) => candidate.type === 'currency');
    return part?.value || country.fiatCurrency;
  } catch {
    return country.fiatCurrency;
  }
}

/**
 * Money in an **explicit** currency, which may not be the country's fiat.
 *
 * Needed wherever a row carries its own currency — a bet placed in USDT stays
 * USDT regardless of which country is active. Still no per-currency branching:
 * `Intl` throws on a code it does not know (USDT and every other crypto ticker
 * are not ISO 4217), and that throw is the signal to fall back to the generic
 * `<amount> <CODE>` form. One rule covers every present and future coin, where
 * `if (currency === 'USDT')` covered exactly one.
 *
 * Grouping still follows the active country, so a Brazilian and an Indian reading
 * the same USDT figure each get their own separators.
 */
export function fmtCurrency(
  value: number | null | undefined,
  currency: string,
  country: CountryConfig = resolveCountry(),
  /**
   * 整数也保留小数位。
   *
   * <p>默认行为是整数不带小数（`R$ 60.237`），非整数才带（`R$ 60.237,25`）——
   * 对静态金额没问题，但**实时变动的数字会因此横向跳动**：同一个奖池数字，
   * 整数与带分两种写法在 mono 下相差 42px，而它是居中的。奖池每有一笔下注就
   * 变一次，于是每跨越一次整数边界就抖一下。
   *
   * <p>会自己变的金额（奖池、注入微动）传 true。
   */
  alwaysCents = false,
): string {
  const amount = Number(value);
  const safe = value === null || value === undefined || Number.isNaN(amount) ? 0 : amount;
  const locale = moneyLocale(country);
  const code = String(currency || country.fiatCurrency).toUpperCase();
  // `alwaysCents` pins the width of a live number; it does not add cents a
  // market does not use. Argentina's 0 display decimals win here too, so its
  // jackpot reads `$ 18.093.000` and Brazil's keeps its `,50`.
  const decimals = !alwaysCents && Number.isInteger(safe)
    ? 0 : currencyDecimals(locale, code, country);
  try {
    return joinCurrencyParts(formatter(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).formatToParts(safe), code, country);
  } catch {
    return `${formatter(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(safe)} ${code}`;
  }
}

/** Grouped amount with no currency symbol — for rows that label the currency once. */
export function fmtAmount(value: number | null | undefined, country: CountryConfig = resolveCountry()): string {
  const amount = Number(value);
  const locale = moneyLocale(country);
  const safe = value === null || value === undefined || Number.isNaN(amount) ? 0 : amount;
  const decimals = Number.isInteger(safe) ? 0 : currencyDecimals(locale, country.fiatCurrency, country);
  try {
    return formatter(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(safe);
  } catch {
    return safe.toFixed(decimals);
  }
}

/** Grouped integer — counts, not money, but grouping is still per-locale. */
/**
 * A rate shown as a percentage number (the caller adds the "%"): trailing
 * zeros are dropped, so 1.5 reads "1,5" and 1 reads "1" — not the "1,50" that
 * money formatting would give, which looks like a price.
 */
export function fmtPercent(value: number | null | undefined, country: CountryConfig = resolveCountry()): string {
  const amount = Number(value);
  const safe = value === null || value === undefined || Number.isNaN(amount) ? 0 : amount;
  const locale = moneyLocale(country);
  try {
    return formatter(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(safe);
  } catch {
    return String(Number(safe.toFixed(2)));
  }
}

export function fmtInt(value: number | null | undefined, country: CountryConfig = resolveCountry()): string {
  const safe = Number(value || 0);
  try {
    return formatter(moneyLocale(country), { maximumFractionDigits: 0 }).format(safe);
  } catch {
    return String(safe);
  }
}

/**
 * CLDR's Spanish patterns abbreviate 1.000–9.999 with an uppercase `K` and
 * 10.000+ with a lowercase `k`, so one payout row comes out as
 * `$3 K · $22,5 k · $225 k`. Uppercasing settles that, but only when the unit is
 * a single letter: Portuguese abbreviates with words (`mil`, `mi`) and Indian
 * English with `L` and `Cr`, and shouting those would be wrong.
 */
function normalizeCompactUnit(unit: string): string {
  return unit.length === 1 ? unit.toUpperCase() : unit;
}

/** A formatted amount split into the symbol and the rest, for a label that stacks them. */
export type MoneyParts = { symbol: string; amount: string };

function splitCurrencyParts(parts: Intl.NumberFormatPart[], currency: string, country: CountryConfig): MoneyParts {
  const override = country.fiatSymbol && currency === country.fiatCurrency ? country.fiatSymbol : null;
  let symbol = '';
  let amount = '';
  for (const part of parts) {
    if (part.type === 'currency') symbol = override || part.value;
    else if (part.type === 'compact') amount += normalizeCompactUnit(part.value);
    else if (part.type === 'literal' && !amount) continue; // the space Intl puts between symbol and figure
    else amount += part.value;
  }
  return { symbol, amount: amount.trim() };
}

/**
 * Money abbreviated the way the country's own language abbreviates it.
 *
 * For a label in a fixed-width slot, not for a figure the player transacts on.
 * `$ 3.000.000` is eleven characters and Argentine prizes are routinely that
 * long, which is how the Milhar payout row came to overlap its neighbour: the
 * slot is 54px and the text simply ran past it.
 *
 * The abbreviation is `Intl`'s, so it is the one each market actually reads and
 * nothing here needs a per-country case — Spanish gets `$225 K` and `$3 M`,
 * Portuguese `R$ 225 mil` and `R$ 3 mi`, Indian English `₹2,3 L` and `₹1,8 Cr`
 * rather than a thousands/millions scale that does not exist there.
 *
 * Below `from` (default 1.000) the output is the exact amount with no unit at
 * all, cents included, so a market whose prizes are small (Brazil's 4-digit tier
 * pays `R$ 600 / R$ 40 / R$ 4`) is untouched by this function. A label with
 * more room raises `from`: the Me page net-result tile holds `+AR$ 248.000`
 * comfortably and only abbreviates from a million.
 *
 * One decimal at most, and never a trailing zero: `$3 M`, not `$3,0 M`.
 */
export function fmtCompact(
  value: number | null | undefined,
  currency?: string,
  country: CountryConfig = resolveCountry(),
  from = 1000,
): string {
  const { symbol, amount } = fmtCompactParts(value, currency, country, from);
  // Re-joined the way Intl laid it out for this locale: es-AR writes `$ 600`
  // but `$3 M`; keeping Intl's own literal is what fmtCompactParts drops, so
  // the joined form is rebuilt from the same formatter rather than guessed.
  return symbol ? joinCompact(value, currency, country, from) : amount;
}

/**
 * `fmtCompact` as `{ symbol, amount }`, for a slot too narrow to hold both on
 * one line. The Milhar payout row stacks a small `AR$` over `22,5 K`: the symbol
 * is identical in every slot of the row, so it earns a quiet line of its own
 * rather than a fifth of each slot's width.
 */
export function fmtCompactParts(
  value: number | null | undefined,
  currency?: string,
  country: CountryConfig = resolveCountry(),
  from = 1000,
): MoneyParts {
  const amount = Number(value);
  const safe = value === null || value === undefined || Number.isNaN(amount) ? 0 : amount;
  const locale = moneyLocale(country);
  const code = String(currency || country.fiatCurrency).toUpperCase();
  try {
    return splitCurrencyParts(compactFormatter(locale, code, safe, from, country).formatToParts(safe), code, country);
  } catch {
    // An unknown currency (USDT and every crypto ticker) throws on `style:
    // 'currency'`. Falling back to the exact form is the safe direction: too
    // long beats wrong.
    return { symbol: code, amount: fmtAmount(safe, country) };
  }
}

function joinCompact(value: number | null | undefined, currency: string | undefined, country: CountryConfig, from: number): string {
  const amount = Number(value);
  const safe = value === null || value === undefined || Number.isNaN(amount) ? 0 : amount;
  const locale = moneyLocale(country);
  const code = String(currency || country.fiatCurrency).toUpperCase();
  try {
    return joinCurrencyParts(compactFormatter(locale, code, safe, from, country).formatToParts(safe), code, country);
  } catch {
    return fmtCurrency(safe, code, country);
  }
}

/**
 * The formatter behind fmtCompact: exact below `from` (so cents survive —
 * compact notation would round `R$ 22,50` to `R$ 22,5`), one-decimal compact
 * notation at or above it.
 */
function compactFormatter(locale: string, code: string, safe: number, from: number, country: CountryConfig): Intl.NumberFormat {
  if (Math.abs(safe) < from) {
    const decimals = Number.isInteger(safe) ? 0 : currencyDecimals(locale, code, country);
    return formatter(locale, { style: 'currency', currency: code, minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  return formatter(locale, {
    style: 'currency',
    currency: code,
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}
